import { useEffect, useRef, useState } from "react";

type Options = {
  /** Scroll depth (px) under which the bar is always revealed. */
  revealAbove?: number;
  /** Minimum scroll delta (px) before a direction change flips visibility. */
  threshold?: number;
  /** Pin the bar open regardless of scroll (e.g. while the menu is open). */
  pinned?: boolean;
};

type ScrollState = {
  /** True once the user has scrolled down past `revealAbove`. */
  hidden: boolean;
  /** True as soon as the page is scrolled at all — for backdrop/border. */
  scrolled: boolean;
};

/**
 * Tracks scroll direction for a hide-on-scroll-down / reveal-on-scroll-up bar.
 *
 * Reads are batched into a single rAF per frame so the passive scroll listener
 * never forces layout mid-scroll.
 */
export function useHideOnScroll({
  revealAbove = 80,
  threshold = 8,
  pinned = false,
}: Options = {}): ScrollState {
  const [state, setState] = useState<ScrollState>({
    hidden: false,
    scrolled: false,
  });
  const lastY = useRef(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    lastY.current = Math.max(window.scrollY, 0);

    const update = (): void => {
      frame.current = null;
      // Clamp: iOS rubber-banding reports negative / overscrolled offsets.
      const y = Math.max(window.scrollY, 0);
      const delta = y - lastY.current;
      lastY.current = y;

      setState((prev) => {
        const scrolled = y > 4;
        const hidden =
          pinned || y <= revealAbove
            ? false
            : Math.abs(delta) >= threshold
              ? delta > 0
              : prev.hidden;

        return prev.hidden === hidden && prev.scrolled === scrolled
          ? prev
          : { hidden, scrolled };
      });
    };

    const onScroll = (): void => {
      frame.current ??= requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [revealAbove, threshold, pinned]);

  return state;
}
